/**
 * variantCore.js
 * ----------------------------------------------------------------------------
 * THE one implementation of every product variant rule a client also needs
 * (founder decisions, 4 Oct 2026: sizes and colours are options on the
 * product, every Size x Colour combination can carry its own stock and SKU,
 * and a cart keeps each product + choices combination as its own line).
 *
 * FIVE BYTE-IDENTICAL COPIES. Edit the server copy, then cp it over the others
 * and prove it with cmp:
 *   Lusuku_Dashboard_Node-Server/models/shared/variantCore.js   (source)
 *   Lusuku_Dashboard_Node-Server/frontend/src/utilities/variantCore.js
 *   Lusuku_Expo_Clean/app/utility/variantCore.js
 *   Lusuku_IOS/app/utility/variantCore.js
 *   Lusuku_WebClient V1.0/Lusuku_Web/src/utils/variantCore.js
 * So it imports nothing and touches no platform API: plain functions over
 * plain data. A Mongoose document, a lean row and a JSON payload all read the
 * same way (Map or object for `optionValues`, ObjectId or string for ids).
 *
 * Shapes (models/shared/productVariantFields.js):
 *   variantOptions [{ _id, name, optionGroupId, values: [{ _id, label, enabled }] }]
 *   variants       [{ _id, optionValues: { [optionId]: valueId }, sku,
 *                     priceDelta, countInStock (null = not tracked), enabled,
 *                     image, branch? (decorated per location) }]
 * Legacy brand `specOptions` ([{ fieldKey, required, choices: [{ value,
 * priceDelta, skuSuffix }] }]) are read as options too, so one picker renders
 * both kinds. They have no stock and no variant id.
 *
 * Cart line identity (cartLineId), exact:
 *   <productId>                         plain line (identical to the old key)
 *   <productId>~v:<variantId>           a variant
 *   <productId>~o:<id1>.<id2>           add-on option ids, unique, sorted
 *   <productId>~s:<k1>=<v1>;<k2>=<v2>   legacy specs, sorted by key, encoded
 * Segments always in the order v, o, s; s only when there is no variant.
 * ----------------------------------------------------------------------------
 */

export const VARIANT_LIMITS = Object.freeze({
  maxOptions: 3,
  maxValuesPerOption: 30,
  maxVariants: 200,
  labelMax: 40,
  skuMax: 64,
  optionNameMax: 30,
});

/** Why a variant cannot be ordered. */
export const VARIANT_REASONS = Object.freeze({
  SWITCHED_OFF: "SWITCHED_OFF",
  SOLD_OUT: "SOLD_OUT",
  SOLD_OUT_HERE: "SOLD_OUT_HERE",
  NOT_SOLD_HERE: "NOT_SOLD_HERE",
});

/** How a picker draws one value chip. */
export const VALUE_STATES = Object.freeze({
  AVAILABLE: "available",
  SOLD_OUT: "soldOut",
  OFF: "off",
  NONE: "none",
});

/* -- Small readers ------------------------------------------------------- */

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/** Any id (ObjectId, populated object, string, number) as a string, or "". */
export const idOf = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "object") {
    if (typeof value.toHexString === "function") return value.toHexString();
    if (value._id !== undefined && value._id !== null && value._id !== value) return idOf(value._id);
    if (typeof value.id === "string") return value.id;
    return "";
  }
  return String(value);
};

const num = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const listOf = (value) => (Array.isArray(value) ? value : []);

const blank = (value) => value === null || value === undefined || value === "";

/** [key, value] pairs of a Map (Mongoose or native) or a plain object. */
const entriesOf = (map) => {
  if (!map || typeof map !== "object") return [];
  if (typeof map.get === "function" && typeof map.entries === "function") return Array.from(map.entries());
  return Object.keys(map).map((key) => [key, map[key]]);
};

/** One entry of a Map or a plain object. */
const pickOf = (map, key) => {
  if (!map || typeof map !== "object") return undefined;
  if (typeof map.get === "function" && typeof map.entries === "function") return map.get(key);
  return hasOwn(map, key) ? map[key] : undefined;
};

/** A variant's picks as a plain { optionId: valueId } of strings. */
export const plainPicks = (optionValues) => {
  const out = {};
  entriesOf(optionValues).forEach(([key, value]) => {
    const id = idOf(value);
    if (key && id) out[String(key)] = id;
  });
  return out;
};

/** Is this variant's stock counted? null, undefined and "" mean no. */
export const isTracked = (variant) => Boolean(variant) && !blank(variant.countInStock);

/* -- Options --------------------------------------------------------------- */

const realOptions = (product) =>
  listOf(product && product.variantOptions).map((opt) => ({
    id: idOf(opt),
    name: String((opt && opt.name) || "").trim(),
    legacy: false,
    required: true,
    fieldKey: null,
    values: listOf(opt && opt.values).map((value) => ({
      id: idOf(value),
      label: String((value && value.label) || "").trim(),
      enabled: !(value && value.enabled === false),
      priceDelta: null,
    })),
  }));

const templateFields = (specTemplate) =>
  Array.isArray(specTemplate) ? specTemplate : listOf(specTemplate && specTemplate.fields);

const legacyOptions = (product, specTemplate) => {
  const fields = templateFields(specTemplate);
  return listOf(product && product.specOptions)
    .filter((opt) => opt && opt.fieldKey && listOf(opt.choices).length > 0)
    .map((opt) => {
      const fieldKey = String(opt.fieldKey);
      const field = fields.find((f) => f && f.fieldKey === fieldKey);
      return {
        id: fieldKey,
        name: String((field && field.label) || fieldKey).trim(),
        legacy: true,
        required: opt.required !== false,
        fieldKey,
        values: listOf(opt.choices)
          .filter((choice) => choice && !blank(choice.value))
          .map((choice) => ({
            id: String(choice.value),
            label: String(choice.value),
            enabled: true,
            priceDelta: num(choice.priceDelta),
          })),
      };
    });
};

/**
 * The product's options in display order: the real `variantOptions`, or the
 * legacy brand `specOptions` read as options (legacy: true, id = fieldKey,
 * value id = the choice value). `specTemplate` is the brand's
 * productSpecTemplate (or its fields) and only supplies the option names.
 */
export const variantOptionsOf = (product, { specTemplate } = {}) => {
  const real = realOptions(product);
  return real.length ? real : legacyOptions(product, specTemplate);
};

/** True when the product sells through variants. */
export const hasVariants = (product) => listOf(product && product.variants).length > 0;

/** The variant with this id, or null. */
export const variantById = (product, variantId) => {
  const id = idOf(variantId);
  if (!id) return null;
  return listOf(product && product.variants).find((v) => v && idOf(v) === id) || null;
};

/** "optId:valId|optId:valId" in option order; the identity of a combination. */
export const combinationKey = (optionValues, options) =>
  listOf(options)
    .map((opt) => {
      const optionId = idOf(opt);
      return `${optionId}:${idOf(pickOf(optionValues, optionId))}`;
    })
    .join("|");

/**
 * Every combination of the options' values, in option order. A combination
 * that already exists keeps its _id, sku, priceDelta, stock, on/off and image,
 * so regenerating after adding a colour never loses what was typed.
 */
export const generateCombinations = (options, existingVariants = []) => {
  const opts = listOf(options)
    .map((opt) => ({ id: idOf(opt), values: listOf(opt && opt.values).map((v) => idOf(v)).filter(Boolean) }))
    .filter((opt) => opt.id && opt.values.length > 0);
  if (!opts.length) return [];
  let combos = [{}];
  opts.forEach((opt) => {
    const next = [];
    combos.forEach((combo) => {
      opt.values.forEach((valueId) => next.push({ ...combo, [opt.id]: valueId }));
    });
    combos = next;
  });
  const existing = new Map();
  listOf(existingVariants).forEach((variant) => {
    if (!variant) return;
    const key = combinationKey(variant.optionValues, opts);
    if (!existing.has(key)) existing.set(key, variant);
  });
  return combos.map((picks) => {
    const found = existing.get(combinationKey(picks, opts));
    if (!found) {
      return { optionValues: picks, sku: null, priceDelta: 0, countInStock: null, enabled: true, image: null };
    }
    return {
      _id: found._id,
      optionValues: picks,
      sku: blank(found.sku) ? null : found.sku,
      priceDelta: num(found.priceDelta),
      countInStock: isTracked(found) ? num(found.countInStock) : null,
      enabled: found.enabled !== false,
      image: blank(found.image) ? null : found.image,
    };
  });
};

/* -- Validation -------------------------------------------------------------- */

const lower = (value) => String(value === null || value === undefined ? "" : value).trim().toLowerCase();

/**
 * Every rule a stored product with variants must meet. The schema, the write
 * normaliser and the dashboard editor all call this, so a vendor sees the
 * same refusal the server would give.
 * @returns {{ ok: boolean, errors: Array<{ code: string, path: string, message: string }> }}
 */
export const validateVariants = (product) => {
  const errors = [];
  const add = (code, path, message) => errors.push({ code, path, message });
  const options = listOf(product && product.variantOptions);
  const variants = listOf(product && product.variants);
  if (!options.length && !variants.length) return { ok: true, errors };

  if (variants.length && listOf(product && product.specOptions).length) {
    add(
      "VARIANTS_AND_SPEC_OPTIONS",
      "variants",
      "A product uses either options with variants or the older buyer choices, not both. Turn the choices into variants first."
    );
  }
  if (options.length > VARIANT_LIMITS.maxOptions) {
    add("TOO_MANY_OPTIONS", "variantOptions", `A product can have at most ${VARIANT_LIMITS.maxOptions} options.`);
  }
  if (variants.length > VARIANT_LIMITS.maxVariants) {
    add("TOO_MANY_VARIANTS", "variants", `A product can have at most ${VARIANT_LIMITS.maxVariants} variants.`);
  }
  if (options.length && !variants.length) {
    add("OPTIONS_WITHOUT_VARIANTS", "variants", "Generate the combinations for these options before saving.");
  }
  if (variants.length && !options.length) {
    add("VARIANTS_WITHOUT_OPTIONS", "variantOptions", "Variants need at least one option, such as Size.");
  }

  const seenNames = new Set();
  const valueIdsByOption = new Map();
  options.forEach((opt, i) => {
    const name = String((opt && opt.name) || "").trim();
    const path = `variantOptions.${i}`;
    if (!name) add("OPTION_NAME_REQUIRED", `${path}.name`, "Every option needs a name, such as Size or Colour.");
    else if (name.length > VARIANT_LIMITS.optionNameMax) {
      add("OPTION_NAME_TOO_LONG", `${path}.name`, `"${name}" is longer than ${VARIANT_LIMITS.optionNameMax} characters.`);
    } else if (seenNames.has(lower(name))) add("DUPLICATE_OPTION", `${path}.name`, `The option "${name}" is listed twice.`);
    seenNames.add(lower(name));

    const values = listOf(opt && opt.values);
    if (!values.length) add("OPTION_WITHOUT_VALUES", `${path}.values`, `${name || "This option"} needs at least one value.`);
    if (values.length > VARIANT_LIMITS.maxValuesPerOption) {
      add("TOO_MANY_VALUES", `${path}.values`, `${name || "An option"} can have at most ${VARIANT_LIMITS.maxValuesPerOption} values.`);
    }
    const seenLabels = new Set();
    const ids = new Set();
    values.forEach((value, j) => {
      const label = String((value && value.label) || "").trim();
      const vpath = `${path}.values.${j}.label`;
      if (!label) add("VALUE_LABEL_REQUIRED", vpath, `Every value of ${name || "an option"} needs a label.`);
      else if (label.length > VARIANT_LIMITS.labelMax) {
        add("VALUE_LABEL_TOO_LONG", vpath, `"${label}" is longer than ${VARIANT_LIMITS.labelMax} characters.`);
      } else if (seenLabels.has(lower(label))) add("DUPLICATE_VALUE", vpath, `${name || "An option"} has the value "${label}" twice.`);
      seenLabels.add(lower(label));
      const id = idOf(value);
      if (id) ids.add(id);
    });
    valueIdsByOption.set(idOf(opt), ids);
  });

  const price = Number(product && product.price);
  const quoteOnly = Boolean(product && product.priceOnRequest === true);
  const seenKeys = new Set();
  const seenSkus = new Set();
  variants.forEach((variant, i) => {
    const path = `variants.${i}`;
    const picks = plainPicks(variant && variant.optionValues);
    let complete = true;
    options.forEach((opt) => {
      const optionId = idOf(opt);
      const picked = picks[optionId];
      if (!picked) {
        complete = false;
        add("VARIANT_VALUE_MISSING", `${path}.optionValues`, `Variant ${i + 1} has no ${String((opt && opt.name) || "option value")}.`);
      } else if (!(valueIdsByOption.get(optionId) || new Set()).has(picked)) {
        complete = false;
        add("VARIANT_VALUE_UNKNOWN", `${path}.optionValues`, `Variant ${i + 1} names a ${String((opt && opt.name) || "value")} that is not in the list.`);
      }
    });
    Object.keys(picks).forEach((optionId) => {
      if (!valueIdsByOption.has(optionId)) {
        complete = false;
        add("VARIANT_OPTION_UNKNOWN", `${path}.optionValues`, `Variant ${i + 1} names an option the product does not have.`);
      }
    });
    if (complete && options.length) {
      const key = combinationKey(picks, options);
      if (seenKeys.has(key)) add("DUPLICATE_COMBINATION", path, `Variant ${i + 1} repeats a combination that is already listed.`);
      seenKeys.add(key);
    }

    const sku = variant && !blank(variant.sku) ? String(variant.sku).trim() : "";
    if (sku) {
      if (sku.length > VARIANT_LIMITS.skuMax) add("SKU_TOO_LONG", `${path}.sku`, `SKU "${sku}" is longer than ${VARIANT_LIMITS.skuMax} characters.`);
      else if (seenSkus.has(lower(sku))) add("DUPLICATE_SKU", `${path}.sku`, `SKU "${sku}" is used by two variants.`);
      seenSkus.add(lower(sku));
    }

    const rawDelta = variant ? variant.priceDelta : 0;
    const delta = blank(rawDelta) ? 0 : Number(rawDelta);
    if (!Number.isFinite(delta)) {
      add("PRICE_DELTA_INVALID", `${path}.priceDelta`, `Variant ${i + 1} has a price change that is not a number.`);
    } else if (!quoteOnly) {
      // No item may sell at 0 (founder rule, 29 Sep 2026). An unpriced draft
      // (price 0, not yet live) may not carry a cut either.
      const priced = Number.isFinite(price) && price > 0;
      if ((priced && price + delta <= 0) || (!priced && delta < 0)) {
        add("VARIANT_PRICE_NOT_POSITIVE", `${path}.priceDelta`, `Variant ${i + 1} would sell at or below zero.`);
      }
    }

    if (isTracked(variant)) {
      const count = Number(variant.countInStock);
      if (!Number.isInteger(count) || count < 0) {
        add("STOCK_INVALID", `${path}.countInStock`, `Variant ${i + 1} needs a whole stock count of 0 or more, or blank for not counted.`);
      }
    }
  });

  return { ok: errors.length === 0, errors };
};

/* -- Reading one variant ------------------------------------------------------ */

/** The variant matching every option's pick, or null while any is unpicked. */
export const findVariant = (product, picks) => {
  if (!hasVariants(product)) return null;
  const options = realOptions(product);
  if (!options.length) return null;
  const wanted = {};
  for (const opt of options) {
    const id = idOf(pickOf(picks, opt.id));
    if (!id) return null;
    wanted[opt.id] = id;
  }
  return (
    product.variants.find((variant) => {
      if (!variant) return false;
      const have = plainPicks(variant.optionValues);
      return options.every((opt) => have[opt.id] === wanted[opt.id]);
    }) || null
  );
};

const chosenValues = (product, variant) => {
  const picks = plainPicks(variant && variant.optionValues);
  return realOptions(product)
    .map((opt) => ({ opt, value: opt.values.find((v) => v.id === picks[opt.id]) || null }))
    .filter((entry) => entry.value);
};

/** "M / Red", in option order. */
export const variantLabel = (product, variant) =>
  chosenValues(product, variant)
    .map((entry) => entry.value.label)
    .join(" / ");

/** { Size: "M", Colour: "Red" }, written to the order line's selectedSpecs. */
export const variantValuesSnapshot = (product, variant) => {
  const out = {};
  chosenValues(product, variant).forEach((entry) => {
    out[entry.opt.name || entry.opt.id] = entry.value.label;
  });
  return out;
};

/** Switched off at head office: the variant itself or any of its values. */
export const variantSwitchedOff = (product, variant) => {
  if (!variant || variant.enabled === false) return true;
  return chosenValues(product, variant).some((entry) => entry.value.enabled === false);
};

/**
 * May this variant be ordered right now? Reads the product and variant
 * switches, head office stock (a tracked count of 0) and the per-location
 * `branch` the storefront decorates onto the variant (or onto the product,
 * which blocks every variant there).
 * @returns {{ orderable: boolean, reason: string|null, soldOutUntil: string|null, here: boolean }}
 */
export const variantOrderable = (product, variant) => {
  const verdict = (reason, extra = {}) => ({ orderable: reason === null, reason, soldOutUntil: null, here: false, ...extra });
  if (!variant || (product && product.enabled === false) || variantSwitchedOff(product, variant)) {
    return verdict(VARIANT_REASONS.SWITCHED_OFF);
  }
  if (isTracked(variant) && num(variant.countInStock) <= 0) return verdict(VARIANT_REASONS.SOLD_OUT);
  const productBranch = product && product.branch && product.branch.orderable === false ? product.branch : null;
  const branch = variant.branch && variant.branch.orderable === false ? variant.branch : productBranch;
  if (branch) {
    if (branch.reason === VARIANT_REASONS.NOT_SOLD_HERE) return verdict(VARIANT_REASONS.NOT_SOLD_HERE, { here: true });
    if (branch.reason === VARIANT_REASONS.SOLD_OUT || branch.reason === VARIANT_REASONS.SOLD_OUT_HERE) {
      return verdict(VARIANT_REASONS.SOLD_OUT_HERE, { here: true, soldOutUntil: branch.soldOutUntil || null });
    }
    return verdict(VARIANT_REASONS.SWITCHED_OFF, { here: true });
  }
  return verdict(null);
};

/**
 * How each value chip should look given what is already picked: a value is
 * judged over the variants that carry it AND agree with the picks on every
 * other option.
 * @returns {{ [optionId]: { [valueId]: "available"|"soldOut"|"off"|"none" } }}
 */
export const valueStates = (product, picks = {}) => {
  const options = variantOptionsOf(product);
  const out = {};
  if (!hasVariants(product)) {
    options.forEach((opt) => {
      out[opt.id] = {};
      opt.values.forEach((value) => {
        if (opt.legacy) out[opt.id][value.id] = value.enabled ? VALUE_STATES.AVAILABLE : VALUE_STATES.OFF;
        else out[opt.id][value.id] = VALUE_STATES.NONE;
      });
    });
    return out;
  }
  const chosen = {};
  options.forEach((opt) => {
    const id = idOf(pickOf(picks, opt.id));
    if (id) chosen[opt.id] = id;
  });
  const judged = product.variants
    .filter(Boolean)
    .map((variant) => ({ picks: plainPicks(variant.optionValues), verdict: variantOrderable(product, variant) }));
  options.forEach((opt) => {
    out[opt.id] = {};
    opt.values.forEach((value) => {
      if (!value.enabled) {
        out[opt.id][value.id] = VALUE_STATES.OFF;
        return;
      }
      const matching = judged.filter(
        (entry) =>
          entry.picks[opt.id] === value.id &&
          options.every((other) => other.id === opt.id || !chosen[other.id] || entry.picks[other.id] === chosen[other.id])
      );
      let state = VALUE_STATES.NONE;
      if (matching.some((entry) => entry.verdict.orderable)) state = VALUE_STATES.AVAILABLE;
      else if (
        matching.some(
          (entry) => entry.verdict.reason === VARIANT_REASONS.SOLD_OUT || entry.verdict.reason === VARIANT_REASONS.SOLD_OUT_HERE
        )
      ) {
        state = VALUE_STATES.SOLD_OUT;
      } else if (matching.length) state = VALUE_STATES.OFF;
      out[opt.id][value.id] = state;
    });
  });
  return out;
};

/**
 * The per-unit price change of a buyer's choice: the variant's priceDelta,
 * else the legacy spec deltas exactly as specPriceDeltaFor sums them.
 */
export const choiceDelta = (product, { variantId, selectedSpecs } = {}) => {
  if (!blank(idOf(variantId))) {
    const variant = variantById(product, variantId);
    return variant ? num(variant.priceDelta) : 0;
  }
  const picks = selectedSpecs || {};
  return listOf(product && product.specOptions).reduce((sum, opt) => {
    const picked = opt ? pickOf(picks, opt.fieldKey) : undefined;
    if (!picked) return sum;
    const choice = listOf(opt.choices).find((c) => c && c.value === picked);
    return sum + (Number(choice && choice.priceDelta) || 0);
  }, 0);
};

const shownBase = (product) => {
  if (!product || blank(product.price)) return null;
  const sale = Number(product.salePrice);
  if (!blank(product.salePrice) && Number.isFinite(sale) && sale > 0) return sale;
  const price = Number(product.price);
  return Number.isFinite(price) ? price : null;
};

/**
 * The cheapest and dearest unit price a shopper can choose, for "From {min}"
 * on a card. Variants: base (sale price while a discount is live; discounts
 * apply to the base only) + delta over the orderable variants, or over all of
 * them when none is orderable. Legacy choices: the cheapest and dearest
 * required picks, optional ones only when they add. Null when the price is
 * hidden or there is nothing to choose.
 */
export const priceRange = (product) => {
  const base = shownBase(product);
  if (base === null) return null;
  if (hasVariants(product)) {
    const variants = product.variants.filter(Boolean);
    const orderable = variants.filter((variant) => variantOrderable(product, variant).orderable);
    const deltas = (orderable.length ? orderable : variants).map((variant) => num(variant.priceDelta));
    if (!deltas.length) return null;
    return { min: base + Math.min(...deltas), max: base + Math.max(...deltas) };
  }
  const options = legacyOptions(product);
  if (!options.length) return null;
  let min = base;
  let max = base;
  options.forEach((opt) => {
    const deltas = opt.values.map((value) => num(value.priceDelta));
    if (!deltas.length) return;
    const low = Math.min(...deltas);
    const high = Math.max(...deltas);
    min += opt.required ? low : Math.min(0, low);
    max += opt.required ? high : Math.max(0, high);
  });
  return { min, max };
};

/** Stock is counted for every variant that is on. */
export const fullyTracked = (product) =>
  listOf(product && product.variants)
    .filter((variant) => variant && !variantSwitchedOff(product, variant))
    .every((variant) => isTracked(variant));

/**
 * The product-level `countInStock` a product with variants should carry, so
 * every reader written before variants stays right: the sum of the counts of
 * the variants that are on when all of them are counted, else at least 1
 * (product-level brand stock is an availability flag). Unchanged without
 * variants.
 */
export const stockMirror = (product) => {
  const current = num(product && product.countInStock);
  if (!hasVariants(product)) return current;
  if (!fullyTracked(product)) return Math.max(1, current);
  return product.variants
    .filter((variant) => variant && !variantSwitchedOff(product, variant))
    .reduce((sum, variant) => sum + Math.max(0, num(variant.countInStock)), 0);
};

/* -- Cart line identity ------------------------------------------------------- */

const optionIdOf = (option) => {
  if (option === null || option === undefined) return "";
  if (typeof option !== "object") return idOf(option);
  if (typeof option.toHexString === "function") return option.toHexString();
  return idOf(
    !blank(option.optionId) ? option.optionId : !blank(option._id) ? option._id : !blank(option.option) ? option.option : option.id
  );
};

const specSegment = (selectedSpecs) =>
  entriesOf(selectedSpecs)
    .filter(([key, value]) => !blank(key) && !blank(value) && typeof value !== "object")
    .map(([key, value]) => [String(key), String(value)])
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join(";");

/**
 * The identity of a cart line: product + variant + add-ons + legacy specs.
 * A plain line's id IS the product id, so carts saved before this keep their
 * keys. Never written into `_id`, which stays the product id.
 */
export const cartLineId = (line = {}) => {
  const source = line || {};
  const productId = idOf(!blank(source.productId) ? source.productId : source._id);
  const variantId = idOf(source.variantId);
  const parts = [productId];
  if (variantId) parts.push(`v:${variantId}`);
  const optionIds = Array.from(new Set(listOf(source.options).map(optionIdOf).filter(Boolean))).sort();
  if (optionIds.length) parts.push(`o:${optionIds.join(".")}`);
  if (!variantId) {
    const specs = specSegment(source.selectedSpecs);
    if (specs) parts.push(`s:${specs}`);
  }
  return parts.join("~");
};

/** A line's id: the stamped one, else computed. */
export const lineIdOf = (line) => (line && !blank(line.lineId) ? String(line.lineId) : cartLineId(line));

const qtyKeyOf = (line) => {
  if (line && !blank(line.qty)) return "qty";
  if (line && !blank(line.quantity)) return "quantity";
  return "qty";
};

const mergeInto = (target, line) => {
  const key = qtyKeyOf(target);
  target[key] = num(target[key]) + num(line[qtyKeyOf(line)]);
};

/**
 * Array carts (app brand and restaurant lines): stamp `lineId` on every line
 * and fold exact duplicates into one by adding their quantities ("qty" or
 * "quantity", whichever the line uses). Nothing is ever dropped; anything that
 * is not a line object passes through untouched. Returns new line objects.
 */
export const withLineIds = (lines) => {
  if (!Array.isArray(lines)) return [];
  const out = [];
  const at = new Map();
  lines.forEach((line) => {
    if (!line || typeof line !== "object") {
      out.push(line);
      return;
    }
    const lineId = cartLineId(line);
    if (at.has(lineId)) {
      mergeInto(out[at.get(lineId)], line);
      return;
    }
    at.set(lineId, out.length);
    out.push({ ...line, lineId });
  });
  return out;
};

/**
 * Map carts (app pharmacy, every website cart: { [key]: line } per vendor):
 * rekey each line by its lineId, adding quantities when two land on one key.
 * A line with no product id of its own keeps its old key as its product id.
 * Plain lines keep their key. Nothing is ever dropped.
 */
export const rekeyLineMap = (map) => {
  if (!map || typeof map !== "object" || Array.isArray(map)) return map;
  const out = {};
  Object.keys(map).forEach((key) => {
    const line = map[key];
    if (!line || typeof line !== "object") {
      if (!hasOwn(out, key)) out[key] = line;
      return;
    }
    const named = !blank(line.productId) || !blank(idOf(line._id));
    const lineId = cartLineId(named ? line : { ...line, productId: key });
    if (hasOwn(out, lineId) && out[lineId] && typeof out[lineId] === "object") {
      mergeInto(out[lineId], line);
      return;
    }
    out[lineId] = { ...line, lineId };
  });
  return out;
};
